import {ReportIntentPayload} from '../ReportBuilderContext';

/** Keys the report-definition contract allows. A saved report's definition arrives with storage fields
 * alongside them (id, owner, company, audit stamps), and the contract validator rejects unknown keys,
 * so the stored shape is narrowed to the contract before it is validated or handed to the provider. */
const DEFINITION_KEYS = [
	'schemaVersion',
	'reportType',
	'name',
	'startDate',
	'endDate',
	'grouping',
	'fields',
	'filter',
	'layout',
	'sort',
] as const;

/** Narrows a stored report definition to the contract shape. `groupings` is the stored plural spelling
 * of the contract's `grouping`; empty slots are dropped, since storage pads them but the contract
 * forbids them. */
export function toReportDefinition(stored: Record<string, unknown>): ReportIntentPayload['definition'] {
	const definition: Record<string, unknown> = {};
	for (const key of DEFINITION_KEYS) {
		if (stored[key] !== undefined && stored[key] !== null) {
			definition[key] = stored[key];
		}
	}
	// A column that was never resized is stored with a null width; the contract has no null, it has an
	// absent width, so the key is dropped rather than carried through as null.
	if (Array.isArray(definition.layout)) {
		definition.layout = (definition.layout as Array<Record<string, unknown>>).map(column =>
			typeof column?.width === 'number' ? {field: column.field, width: column.width} : {field: column?.field}
		);
	}

	// Storage leaves an unused filter group null and types condition values loosely; the contract wants the
	// key absent and the values as strings. It also returns no filter at all for a report with no filter
	// row, while the contract requires one — so an absent filter becomes an empty filter rather than being
	// left out and failing validation.
	const filter = (definition.filter ?? {}) as Record<string, unknown>;
	{
		const conditions = Array.isArray(filter.filterConditions) ? filter.filterConditions : [];
		definition.filter = {
			type: filter.type ?? 'SIMPLE_AND',
			...(typeof filter.filterGroup === 'string' && filter.filterGroup !== '' && {filterGroup: filter.filterGroup}),
			filterConditions: (conditions as Array<Record<string, unknown>>).map(condition => ({
				field: condition.field,
				operator: condition.operator,
				context: condition.context,
				values: (Array.isArray(condition.values) ? condition.values : []).map(value => String(value)),
			})),
		};
	}

	const grouping = (definition.grouping ?? stored.groupings) as unknown[] | undefined;
	if (Array.isArray(grouping)) {
		const populated = grouping.filter(token => typeof token === 'string' && token !== '');
		if (populated.length > 0) {
			definition.grouping = populated;
		} else {
			delete definition.grouping;
		}
	}
	return definition as unknown as ReportIntentPayload['definition'];
}
